import {
  AddOutlined,
  DeleteOutlineOutlined,
  EmailOutlined,
  ForwardToInboxOutlined,
  HubOutlined,
  LinkOffOutlined,
  LockOutlined,
  MultipleStopOutlined,
  RefreshOutlined,
} from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import {
  PartyManagementController,
  PartyQuotationExchangeOperations,
} from 'controllers/System/PartyManagementController'
import {
  SCSNManagementController,
  SCSNManagementOperations,
} from 'controllers/System/SCSNManagementController'
import { useAppController } from 'customHooks/useAppController'
import { useDialogManager } from 'features/DialogManager/useDialogManager'
import { FormEvent, ReactNode, useEffect, useState } from 'react'
import {
  CreatePartyEmailQuotationExchangeChannelRequest,
  EmailConnectionType as GeneratedEmailConnectionType,
  ManagementPartyDto,
  PartyScsnQuotationExchangeChannelDto,
  ProvisionSCSNRequest,
  QuotationExchangeChannel,
  UpdatePartyEmailQuotationExchangeChannelRequest,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import {
  EmailConnectionType,
  PartyEmailChannel,
  PartyEmailChannelController,
  PartyEmailChannelOperations,
  PartyEmailChannelStatus,
} from 'controllers/System/PartyEmailChannelCompatibility'
import {
  getPartyQuotationExchangeChannels,
  isEmailChannel,
  isScsnChannel,
  PartyQuotationExchangeChannel,
} from './partyQuotationExchangeChannels'

type Props = {
  party: ManagementPartyDto
  onPartyChanged: (party: ManagementPartyDto) => void
}

type EmailDraft = Omit<CreatePartyEmailQuotationExchangeChannelRequest, 'connectionType'> & {
  connectionType: EmailConnectionType
}

const emptyEmail: EmailDraft = {
  emailAddress: '',
  displayName: undefined,
  isEnabled: true,
  connectionType: 'Forwarding',
}

const errorMessage = (error: unknown) =>
  typeof error === 'object' && error && 'message' in error
    ? String(error.message)
    : 'The operation could not be completed.'

const emailIsValid = (email: string | undefined) =>
  Boolean(email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))

type ChannelCardProps = {
  icon: ReactNode
  title: string
  subtitle?: string
  children?: ReactNode
  actions?: ReactNode
}

const ChannelCard = ({
  icon,
  title,
  subtitle,
  children,
  actions,
}: ChannelCardProps) => (
  <Paper variant="outlined" component="article" sx={{ p: { xs: 1.5, sm: 2 } }}>
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      sx={{ gap: 1.5, alignItems: { sm: 'flex-start' } }}
    >
      <Box sx={{ color: 'primary.main', display: 'flex', pt: 0.25 }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
        {children}
      </Box>
      {actions && <Box sx={{ flexShrink: 0 }}>{actions}</Box>}
    </Stack>
  </Paper>
)

type EmailAddressEditorProps = {
  partyId: string
  channel: PartyEmailChannel
  loading: Record<string, boolean>
  error?: string
  onSave: (
    channelId: string,
    request: UpdatePartyEmailQuotationExchangeChannelRequest,
  ) => Promise<void>
  onDelete: (channelId: string) => void
}

const EmailAddressEditor = ({
  partyId,
  channel,
  loading,
  error,
  onSave,
  onDelete,
}: EmailAddressEditorProps) => {
  const {
    controller: connectionController,
    loading: connectionLoading,
    error: connectionError,
  } = useAppController(() => new PartyEmailChannelController())
  const [draft, setDraft] = useState<EmailDraft>({
    emailAddress: channel.emailAddress,
    displayName: channel.displayName,
    isEnabled: channel.isEnabled,
    connectionType: channel.connectionType || 'Forwarding',
  })
  const [status, setStatus] = useState<PartyEmailChannelStatus>(channel)
  const [connectionRequestError, setConnectionRequestError] = useState<string>()
  const [showValidation, setShowValidation] = useState(false)

  useEffect(() => {
    setDraft({
      emailAddress: channel.emailAddress,
      displayName: channel.displayName,
      isEnabled: channel.isEnabled,
      connectionType: channel.connectionType || 'Forwarding',
    })
    setStatus(channel)
    setConnectionRequestError(undefined)
    setShowValidation(false)
  }, [channel])

  const updateOperation = PartyQuotationExchangeOperations.updateEmail(
    partyId,
    channel.id,
  )
  const deleteOperation = PartyQuotationExchangeOperations.deleteEmail(
    partyId,
    channel.id,
  )
  const busy = Boolean(loading[updateOperation] || loading[deleteOperation])
  const dirty =
    draft.emailAddress !== channel.emailAddress ||
    (draft.displayName || undefined) !== (channel.displayName || undefined) ||
    draft.isEnabled !== channel.isEnabled ||
    draft.connectionType !== (channel.connectionType || 'Forwarding')

  const statusOperation = PartyEmailChannelOperations.status(
    partyId,
    channel.id,
  )
  const forwardingOperation = PartyEmailChannelOperations.forwarding(
    partyId,
    channel.id,
  )
  const connectOperation = PartyEmailChannelOperations.connectMicrosoft365(
    partyId,
    channel.id,
  )
  const disconnectOperation =
    PartyEmailChannelOperations.disconnectMicrosoft365(partyId, channel.id)
  const connectionBusy = Boolean(
    connectionLoading[statusOperation] ||
    connectionLoading[forwardingOperation] ||
    connectionLoading[connectOperation] ||
    connectionLoading[disconnectOperation],
  )

  const runConnectionAction = async (
    action: () => Promise<PartyEmailChannelStatus>,
  ) => {
    setConnectionRequestError(undefined)
    try {
      setStatus(await action())
    } catch (caught) {
      setConnectionRequestError(errorMessage(caught))
    }
  }

  const connectMicrosoft365 = async () => {
    setConnectionRequestError(undefined)
    try {
      const response = await connectionController.connectMicrosoft365(
        partyId,
        channel.id,
      )
      setStatus(response)
      if (response.authorizationUrl)
        window.location.assign(response.authorizationUrl)
    } catch (caught) {
      setConnectionRequestError(errorMessage(caught))
    }
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setShowValidation(true)
    if (!emailIsValid(draft.emailAddress)) return
    await onSave(channel.id, {
      ...draft,
      connectionType: draft.connectionType as GeneratedEmailConnectionType,
    })
  }

  return (
    <Box
      component="section"
      aria-label={
        channel.displayName || channel.emailAddress || 'Email address'
      }
      sx={{ pt: 2, mt: 2, borderTop: 1, borderColor: 'divider' }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        sx={{
          gap: 1,
          alignItems: { sm: 'center' },
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {channel.displayName || channel.emailAddress || 'Email address'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {channel.isEnabled ? 'Enabled destination' : 'Disabled destination'}
          </Typography>
        </Box>
        <Button
          color="error"
          size="small"
          startIcon={<DeleteOutlineOutlined />}
          disabled={busy}
          onClick={() => onDelete(channel.id)}
        >
          Delete
        </Button>
      </Stack>
      <Box component="form" onSubmit={submit} sx={{ mt: 1.5 }} noValidate>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'minmax(0, 1fr) minmax(0, 1fr) auto',
            },
            gap: 1.5,
            alignItems: 'center',
          }}
        >
          <TextField
            size="small"
            label="Email address"
            value={draft.emailAddress || ''}
            disabled={busy}
            required
            error={showValidation && !emailIsValid(draft.emailAddress)}
            helperText={
              showValidation && !emailIsValid(draft.emailAddress)
                ? 'Enter a valid email address.'
                : undefined
            }
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                emailAddress: event.target.value,
              }))
            }
          />
          <TextField
            size="small"
            label="Display name (optional)"
            value={draft.displayName || ''}
            disabled={busy}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                displayName: event.target.value || undefined,
              }))
            }
          />
          <FormControlLabel
            label="Enabled"
            control={
              <Switch
                size="small"
                checked={draft.isEnabled}
                disabled={busy}
                onChange={(_, checked) =>
                  setDraft((current) => ({ ...current, isEnabled: checked }))
                }
              />
            }
          />
        </Box>
        <Paper variant="outlined" sx={{ p: 1.5, mt: 1.5 }}>
          <Stack spacing={1.25}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              useFlexGap
              sx={{ gap: 1, alignItems: { sm: 'center' }, flexWrap: 'wrap' }}
            >
              <FormControl size="small" sx={{ minWidth: 190 }}>
                <InputLabel>Connection type</InputLabel>
                <Select
                  label="Connection type"
                  value={draft.connectionType}
                  disabled={busy || connectionBusy}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      connectionType: event.target.value as EmailConnectionType,
                    }))
                  }
                >
                  <MenuItem value="Forwarding">Forwarding</MenuItem>
                  <MenuItem value="Microsoft365">Microsoft 365</MenuItem>
                </Select>
              </FormControl>
              {status.provisioningStatus && (
                <Chip
                  size="small"
                  label={`Provisioning: ${status.provisioningStatus}`}
                />
              )}
              {status.verificationStatus && (
                <Chip
                  size="small"
                  label={`Verification: ${status.verificationStatus}`}
                />
              )}
              {status.subscriptionHealth && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Subscription: ${status.subscriptionHealth}`}
                />
              )}
            </Stack>
            {status.forwardingAddress && (
              <Typography variant="body2">
                Forwarding target: <strong>{status.forwardingAddress}</strong>
              </Typography>
            )}
            {status.providerConnectionReference && (
              <Typography variant="caption" color="text.secondary">
                Provider connection: {status.providerConnectionReference}
              </Typography>
            )}
            <Stack direction="row" useFlexGap sx={{ gap: 1, flexWrap: 'wrap' }}>
              {draft.connectionType === 'Forwarding' ? (
                <Button
                  size="small"
                  startIcon={<ForwardToInboxOutlined />}
                  disabled={busy || connectionBusy}
                  onClick={() =>
                    runConnectionAction(() =>
                      connectionController.setupForwarding(partyId, channel.id),
                    )
                  }
                >
                  Set up forwarding
                </Button>
              ) : status.connected ? (
                <Button
                  size="small"
                  color="error"
                  startIcon={<LinkOffOutlined />}
                  disabled={busy || connectionBusy}
                  onClick={() =>
                    runConnectionAction(async () => {
                      await connectionController.disconnectMicrosoft365(
                        partyId,
                        channel.id,
                      )
                      return { ...status, connected: false }
                    })
                  }
                >
                  Disconnect Microsoft 365
                </Button>
              ) : (
                <Button
                  size="small"
                  disabled={busy || connectionBusy}
                  onClick={connectMicrosoft365}
                >
                  Connect Microsoft 365
                </Button>
              )}
              <Button
                size="small"
                startIcon={<RefreshOutlined />}
                disabled={busy || connectionBusy}
                onClick={() =>
                  runConnectionAction(() =>
                    connectionController.status(partyId, channel.id),
                  )
                }
              >
                Refresh status
              </Button>
            </Stack>
            {(status.lastError || connectionRequestError) && (
              <Alert severity="error">
                {status.lastError || connectionRequestError}
              </Alert>
            )}
            {!connectionRequestError &&
              Object.values({
                status: connectionError[statusOperation],
                forwarding: connectionError[forwardingOperation],
                connect: connectionError[connectOperation],
                disconnect: connectionError[disconnectOperation],
              }).some(Boolean) && (
                <Alert severity="error">
                  {Object.values({
                    status: connectionError[statusOperation],
                    forwarding: connectionError[forwardingOperation],
                    connect: connectionError[connectOperation],
                    disconnect: connectionError[disconnectOperation],
                  }).find(Boolean)?.message ||
                    'The mailbox operation could not be completed.'}
                </Alert>
              )}
          </Stack>
        </Paper>
        {error && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {error}
          </Alert>
        )}
        <Stack direction="row" sx={{ mt: 1.5, justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            variant="contained"
            size="small"
            disabled={!dirty || busy}
          >
            {loading[updateOperation] ? 'Saving...' : 'Save email'}
          </Button>
        </Stack>
      </Box>
    </Box>
  )
}

type ScsnFormProps = {
  partyId: string
  channel?: PartyScsnQuotationExchangeChannelDto
  saving: boolean
  error?: string
  onCancel?: () => void
  onProvision: (request: ProvisionSCSNRequest) => Promise<void>
}

const ScsnProvisioningForm = ({
  partyId,
  channel,
  saving,
  error,
  onCancel,
  onProvision,
}: ScsnFormProps) => {
  const [request, setRequest] = useState<ProvisionSCSNRequest>({
    partyId,
    partyName: channel?.partyName || '',
    partyLegalEntityId: channel?.partyLegalEntityID || '',
    partyTaxId: channel?.partyTaxID || '',
  })
  const [provisionNewGln, setProvisionNewGln] = useState(true)
  const [showValidation, setShowValidation] = useState(false)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setShowValidation(true)
    if (
      !request.partyName ||
      !request.partyLegalEntityId ||
      !request.partyTaxId ||
      (!provisionNewGln && !request.partyIdentification)
    )
      return
    await onProvision(request)
  }

  return (
    <ChannelCard
      icon={<HubOutlined fontSize="small" />}
      title="Provision SCSN"
      subtitle="Connect this party to the Smart Connected Supplier Network."
    >
      <Box component="form" onSubmit={submit} sx={{ mt: 2 }} noValidate>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 1.5,
          }}
        >
          <TextField
            size="small"
            label="Party name"
            value={request.partyName}
            disabled={saving}
            required
            error={showValidation && !request.partyName}
            onChange={(event) =>
              setRequest((current) => ({
                ...current,
                partyName: event.target.value,
              }))
            }
          />
          <TextField
            size="small"
            label="Legal entity ID"
            value={request.partyLegalEntityId}
            disabled={saving}
            required
            error={showValidation && !request.partyLegalEntityId}
            onChange={(event) =>
              setRequest((current) => ({
                ...current,
                partyLegalEntityId: event.target.value,
              }))
            }
          />
          <TextField
            size="small"
            label="Tax ID"
            value={request.partyTaxId}
            disabled={saving}
            required
            error={showValidation && !request.partyTaxId}
            onChange={(event) =>
              setRequest((current) => ({
                ...current,
                partyTaxId: event.target.value,
              }))
            }
          />
          <TextField
            size="small"
            label="GLN"
            value={request.partyIdentification || ''}
            disabled={saving || provisionNewGln}
            required={!provisionNewGln}
            error={
              showValidation && !provisionNewGln && !request.partyIdentification
            }
            onChange={(event) =>
              setRequest((current) => ({
                ...current,
                partyIdentification: event.target.value || undefined,
              }))
            }
          />
          <FormControlLabel
            label="Provision new GLN"
            control={
              <Checkbox
                checked={provisionNewGln}
                disabled={saving}
                onChange={(_, checked) => {
                  setProvisionNewGln(checked)
                  if (checked) {
                    setRequest((current) => ({
                      ...current,
                      partyIdentification: undefined,
                    }))
                  }
                }}
              />
            }
          />
        </Box>
        {error && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {error}
          </Alert>
        )}
        <Stack
          direction="row"
          sx={{ gap: 1, mt: 1.5, justifyContent: 'flex-end' }}
        >
          {onCancel && (
            <Button onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
          )}
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? 'Provisioning...' : 'Provision SCSN'}
          </Button>
        </Stack>
      </Box>
    </ChannelCard>
  )
}

export const PartyQuotationExchangeChannelsTab = ({
  party,
  onPartyChanged,
}: Props) => {
  const { openDialog } = useDialogManager()
  const { controller, loading, error } = useAppController(
    () => new PartyManagementController(),
  )
  const {
    controller: scsnController,
    loading: scsnLoading,
    error: scsnError,
  } = useAppController(() => new SCSNManagementController())
  const [addingEmail, setAddingEmail] = useState(false)
  const [addingScsn, setAddingScsn] = useState(false)
  const [emailDraft, setEmailDraft] = useState<EmailDraft>(emptyEmail)
  const [showEmailValidation, setShowEmailValidation] = useState(false)
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({})

  const channels = getPartyQuotationExchangeChannels(party)
  const scsn = channels.find(isScsnChannel)
  const emailChannels = channels.filter(isEmailChannel)
  const blockers = channels.filter((channel) =>
    [QuotationExchangeChannel.Scsn, QuotationExchangeChannel.Email].includes(
      channel.channel,
    ),
  )

  const run = async (
    operation: string,
    action: () => Promise<ManagementPartyDto>,
    afterSuccess?: () => void,
  ) => {
    setLocalErrors((current) => ({ ...current, [operation]: '' }))
    try {
      const replacement = await action()
      onPartyChanged(replacement)
      afterSuccess?.()
    } catch (caught) {
      setLocalErrors((current) => ({
        ...current,
        [operation]: errorMessage(caught),
      }))
    }
  }

  const operationError = (operation: string) =>
    localErrors[operation] ||
    error[operation]?.message ||
    scsnError[operation]?.message

  const confirm = (title: string, message: string, action: () => void) =>
    openDialog('ConfirmationDialog', { title, message, onConfirm: action })

  if (!party.id) {
    return (
      <Alert severity="info">
        Save the party in General before configuring quotation channels.
      </Alert>
    )
  }

  const enableOperation = PartyQuotationExchangeOperations.enable(party.id)
  const disableOperation = PartyQuotationExchangeOperations.disable(party.id)
  const createEmailOperation = PartyQuotationExchangeOperations.createEmail(
    party.id,
  )
  const provisionOperation = SCSNManagementOperations.provision(party.id)
  const deprovisionOperation = SCSNManagementOperations.deprovision(party.id)

  if (channels.length === 0) {
    return (
      <Paper
        variant="outlined"
        sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center' }}
      >
        <MultipleStopOutlined color="disabled" sx={{ fontSize: 38 }} />
        <Typography variant="h6" sx={{ mt: 1 }}>
          Quotation exchange is disabled
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
          Enable the feature to start with the Manual quotation channel.
        </Typography>
        {operationError(enableOperation) && (
          <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
            {operationError(enableOperation)}
          </Alert>
        )}
        <Button
          variant="contained"
          disabled={loading[enableOperation]}
          onClick={() =>
            run(enableOperation, () =>
              controller.EnableQuotationExchange(party.id),
            )
          }
        >
          {loading[enableOperation]
            ? 'Enabling...'
            : 'Enable quotation exchange'}
        </Button>
      </Paper>
    )
  }

  const createEmail = async (event: FormEvent) => {
    event.preventDefault()
    setShowEmailValidation(true)
    if (!emailIsValid(emailDraft.emailAddress)) return
    await run(
      createEmailOperation,
      () =>
        controller.CreateEmailQuotationExchangeChannel(party.id, {
          ...emailDraft,
          connectionType:
            emailDraft.connectionType as GeneratedEmailConnectionType,
        }),
      () => {
        setAddingEmail(false)
        setEmailDraft(emptyEmail)
        setShowEmailValidation(false)
      },
    )
  }

  const channelRenderers: Record<
    string,
    (channel: PartyQuotationExchangeChannel) => ReactNode
  > = {
    [QuotationExchangeChannel.Manual]: () => (
      <ChannelCard
        icon={<MultipleStopOutlined fontSize="small" />}
        title="Manual"
        subtitle="Users exchange quotation documents manually."
        actions={
          <Button
            size="small"
            color="error"
            disabled={Boolean(blockers.length || loading[disableOperation])}
            onClick={() =>
              confirm(
                'Disable quotation exchange?',
                'This removes the Manual channel and disables quotation exchange.',
                () =>
                  run(disableOperation, () =>
                    controller.DisableQuotationExchange(party.id),
                  ),
              )
            }
          >
            {loading[disableOperation] ? 'Disabling...' : 'Disable feature'}
          </Button>
        }
      >
        {blockers.length > 0 && (
          <Alert
            icon={<LockOutlined fontSize="inherit" />}
            severity="info"
            sx={{ mt: 1.5 }}
          >
            Remove{' '}
            {[scsn && 'SCSN', channels.some(isEmailChannel) && 'Email']
              .filter(Boolean)
              .join(' and ')}{' '}
            channels before disabling quotation exchange.
          </Alert>
        )}
        {operationError(disableOperation) && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {operationError(disableOperation)}
          </Alert>
        )}
      </ChannelCard>
    ),
    [QuotationExchangeChannel.Scsn]: (channel) => {
      const scsnChannel = channel as PartyScsnQuotationExchangeChannelDto
      return scsnChannel.isProvisioned ? (
        <ChannelCard
          icon={<HubOutlined fontSize="small" />}
          title="SCSN"
          subtitle="Provisioned and ready for network quotation exchange."
          actions={
            <Button
              size="small"
              color="error"
              disabled={scsnLoading[deprovisionOperation]}
              onClick={() =>
                confirm(
                  'Deprovision SCSN?',
                  'The party will no longer exchange quotations through SCSN.',
                  () =>
                    run(deprovisionOperation, () =>
                      scsnController.deProvisionSCSN({ partyId: party.id }),
                    ),
                )
              }
            >
              {scsnLoading[deprovisionOperation]
                ? 'Deprovisioning...'
                : 'Deprovision'}
            </Button>
          }
        >
          <Stack
            direction="row"
            useFlexGap
            sx={{ mt: 1.5, gap: 1, flexWrap: 'wrap' }}
          >
            <Chip
              size="small"
              label={scsnChannel.partyName || 'Unnamed party'}
            />
            <Chip
              size="small"
              variant="outlined"
              label={scsnChannel.partyIdentification || 'No identification'}
            />
          </Stack>
          {operationError(deprovisionOperation) && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {operationError(deprovisionOperation)}
            </Alert>
          )}
        </ChannelCard>
      ) : (
        <ScsnProvisioningForm
          partyId={party.id}
          channel={scsnChannel}
          saving={Boolean(scsnLoading[provisionOperation])}
          error={operationError(provisionOperation)}
          onProvision={(request) =>
            run(provisionOperation, () => scsnController.provisionSCSN(request))
          }
        />
      )
    },
  }

  return (
    <Stack sx={{ gap: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        sx={{ gap: 1, justifyContent: 'flex-end' }}
      >
        {!scsn && (
          <Button
            startIcon={<HubOutlined />}
            onClick={() => setAddingScsn(true)}
            disabled={addingScsn}
          >
            Add SCSN
          </Button>
        )}
        {emailChannels.length === 0 && (
          <Button
            startIcon={<AddOutlined />}
            onClick={() => setAddingEmail(true)}
            disabled={addingEmail}
          >
            Add email
          </Button>
        )}
      </Stack>

      {channels
        .filter((channel) => !isEmailChannel(channel))
        .map((channel) => {
          const renderer = channelRenderers[channel.channel]
          return (
            <Box key={channel.id}>
              {renderer ? (
                renderer(channel)
              ) : (
                <ChannelCard
                  icon={<LockOutlined fontSize="small" />}
                  title={`Unknown channel: ${String(channel.channel)}`}
                  subtitle="This channel type is not supported by this version of the application. It is shown read-only."
                >
                  <Chip
                    size="small"
                    variant="outlined"
                    label={channel.isEnabled ? 'Enabled' : 'Disabled'}
                    sx={{ mt: 1.5 }}
                  />
                </ChannelCard>
              )}
            </Box>
          )
        })}

      {(emailChannels.length > 0 || addingEmail) && (
        <ChannelCard
          icon={<EmailOutlined fontSize="small" />}
          title="Email"
          subtitle={`${emailChannels.length} ${emailChannels.length === 1 ? 'address' : 'addresses'} configured`}
          actions={
            <Button
              size="small"
              startIcon={<AddOutlined />}
              onClick={() => setAddingEmail(true)}
              disabled={addingEmail}
            >
              Add address
            </Button>
          }
        >
          {emailChannels.map((emailChannel) => {
            const updateOperation =
              PartyQuotationExchangeOperations.updateEmail(
                party.id,
                emailChannel.id,
              )
            const deleteOperation =
              PartyQuotationExchangeOperations.deleteEmail(
                party.id,
                emailChannel.id,
              )

            return (
              <EmailAddressEditor
                key={emailChannel.id}
                partyId={party.id}
                channel={emailChannel}
                loading={loading}
                error={
                  operationError(updateOperation) ||
                  operationError(deleteOperation)
                }
                onSave={(channelId, request) =>
                  run(updateOperation, () =>
                    controller.UpdateEmailQuotationExchangeChannel(
                      party.id,
                      channelId,
                      request,
                    ),
                  )
                }
                onDelete={(channelId) =>
                  confirm(
                    'Delete email address?',
                    `Remove ${emailChannel.emailAddress || 'this email address'}?`,
                    () =>
                      run(deleteOperation, () =>
                        controller.DeleteEmailQuotationExchangeChannel(
                          party.id,
                          channelId,
                        ),
                      ),
                  )
                }
              />
            )
          })}

          {addingEmail && (
            <Box
              component="form"
              onSubmit={createEmail}
              noValidate
              sx={{ pt: 2, mt: 2, borderTop: 1, borderColor: 'divider' }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                New address
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(3, minmax(0, 1fr)) auto',
                  },
                  gap: 1.5,
                  alignItems: 'center',
                }}
              >
                <TextField
                  size="small"
                  label="Email address"
                  value={emailDraft.emailAddress || ''}
                  required
                  disabled={loading[createEmailOperation]}
                  error={
                    showEmailValidation &&
                    !emailIsValid(emailDraft.emailAddress)
                  }
                  helperText={
                    showEmailValidation &&
                    !emailIsValid(emailDraft.emailAddress)
                      ? 'Enter a valid email address.'
                      : undefined
                  }
                  onChange={(event) =>
                    setEmailDraft((current) => ({
                      ...current,
                      emailAddress: event.target.value,
                    }))
                  }
                />
                <FormControl size="small" fullWidth>
                  <InputLabel>Connection type</InputLabel>
                  <Select
                    label="Connection type"
                    value={emailDraft.connectionType}
                    disabled={loading[createEmailOperation]}
                    onChange={(event) =>
                      setEmailDraft((current) => ({
                        ...current,
                        connectionType: event.target
                          .value as EmailConnectionType,
                      }))
                    }
                  >
                    <MenuItem value="Forwarding">Forwarding</MenuItem>
                    <MenuItem value="Microsoft365">Microsoft 365</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Display name (optional)"
                  value={emailDraft.displayName || ''}
                  disabled={loading[createEmailOperation]}
                  onChange={(event) =>
                    setEmailDraft((current) => ({
                      ...current,
                      displayName: event.target.value || undefined,
                    }))
                  }
                />
                <FormControlLabel
                  label="Enabled"
                  control={
                    <Switch
                      size="small"
                      checked={emailDraft.isEnabled}
                      disabled={loading[createEmailOperation]}
                      onChange={(_, checked) =>
                        setEmailDraft((current) => ({
                          ...current,
                          isEnabled: checked,
                        }))
                      }
                    />
                  }
                />
              </Box>
              {operationError(createEmailOperation) && (
                <Alert severity="error" sx={{ mt: 1.5 }}>
                  {operationError(createEmailOperation)}
                </Alert>
              )}
              <Stack
                direction="row"
                sx={{ gap: 1, mt: 1.5, justifyContent: 'flex-end' }}
              >
                <Button
                  disabled={loading[createEmailOperation]}
                  onClick={() => setAddingEmail(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading[createEmailOperation]}
                >
                  {loading[createEmailOperation] ? 'Adding...' : 'Add address'}
                </Button>
              </Stack>
            </Box>
          )}
        </ChannelCard>
      )}

      {addingScsn && !scsn && (
        <ScsnProvisioningForm
          partyId={party.id}
          saving={Boolean(scsnLoading[provisionOperation])}
          error={operationError(provisionOperation)}
          onCancel={() => setAddingScsn(false)}
          onProvision={(request) =>
            run(
              provisionOperation,
              () => scsnController.provisionSCSN(request),
              () => setAddingScsn(false),
            )
          }
        />
      )}
    </Stack>
  )
}
